import React, { useState, useMemo } from 'react';
import { useSaaSContext } from '../../store/SaaSContext';
import { useToast } from '../../store/ToastContext';
import { 
  Sparkles, 
  Plus, 
  Calendar, 
  CalendarCheck, 
  Barcode, 
  CheckCircle2, 
  XCircle, 
  ShieldCheck, 
  ListOrdered, 
  Hash, 
  Clock, 
  Wand2, 
  RotateCcw 
} from 'lucide-react';
import { 
  validateBarcode, 
  validateBarcodeColumn, 
  repairBarcodeDatasetColumn 
} from '../../utils/barcodeValidation';

interface InAppVariableGeneratorPanelProps {
  onClose?: () => void;
}

export const InAppVariableGeneratorPanel: React.FC<InAppVariableGeneratorPanelProps> = ({ onClose }) => {
  const { csvData, setCsvData } = useSaaSContext();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<'serializer' | 'dates' | 'barcodes'>('serializer');

  // --- TAB 1: AUTO SEQUENCE / SERIALIZER STATE ---
  const [seqTargetCol, setSeqTargetCol] = useState('SerialNumber');
  const seqIsNewCol = true;
  const [seqPrefix, setSeqPrefix] = useState('SN-2026-');
  const [seqSuffix, setSeqSuffix] = useState('');
  const [seqStart, setSeqStart] = useState(1);
  const [seqStep, setSeqStep] = useState(1);
  const [seqPadding, setSeqPadding] = useState(4); // 4 digits e.g. 0001
  const [seqRecordCount, setSeqRecordCount] = useState(csvData ? csvData.rows.length : 100);

  // Compute Live Range Preview
  const sequencePreview = useMemo(() => {
    const total = csvData ? csvData.rows.length : seqRecordCount;
    const items: string[] = [];
    const count = Math.min(total, 6);

    for (let i = 0; i < count; i++) {
      const idx = i < 3 ? i : total - (6 - i);
      const val = seqStart + (idx * seqStep);
      const paddedVal = String(val).padStart(seqPadding, '0');
      items.push(`${seqPrefix}${paddedVal}${seqSuffix}`);
    }

    const first3 = items.slice(0, 3);
    const last3 = total > 3 ? items.slice(Math.max(3, items.length - 3)) : [];

    return {
      total,
      first3,
      last3,
      sampleFirst: first3[0] || '',
      sampleLast: last3[last3.length - 1] || first3[first3.length - 1] || ''
    };
  }, [seqPrefix, seqSuffix, seqStart, seqStep, seqPadding, seqRecordCount, csvData]);

  const handleGenerateSequence = () => {
    const colName = seqIsNewCol ? seqTargetCol.trim() : seqTargetCol;
    if (!colName) {
      toast.error('Missing Column Name', 'Please provide a target column name for the serial sequence.');
      return;
    }

    const rowCount = csvData ? csvData.rows.length : seqRecordCount;
    const newRows = csvData ? csvData.rows.map(r => ({ ...r })) : Array(rowCount).fill(null).map(() => ({}));

    for (let i = 0; i < rowCount; i++) {
      const val = seqStart + (i * seqStep);
      const paddedVal = String(val).padStart(seqPadding, '0');
      const seqStr = `${seqPrefix}${paddedVal}${seqSuffix}`;
      newRows[i] = { ...newRows[i], [colName]: seqStr };
    }

    const newHeaders = csvData 
      ? Array.from(new Set([...csvData.headers, colName])) 
      : [colName];

    setCsvData({ headers: newHeaders, rows: newRows });

    toast.success(
      'Sequence Generated Successfully',
      `Generated ${rowCount.toLocaleString()} sequential values for column "${colName}" (${sequencePreview.sampleFirst} → ${sequencePreview.sampleLast}).`,
      { duration: 4000 }
    );
  };

  // --- TAB 2: DYNAMIC DATE & EXPIRY BATCH GENERATOR STATE ---
  const dateMfgOffset = 0; // days from today
  const [dateExpiryMonths, setDateExpiryMonths] = useState(24); // +24 Months default
  const [dateFormat, setDateFormat] = useState<'DD/MM/YYYY' | 'YYYY-MM-DD' | 'DD-MMM-YYYY' | 'MM/YY'>('DD/MM/YYYY');
  const [batchPrefix, setBatchPrefix] = useState('B2026-');
  const includeBatchNo = true;

  // Helper date formatter
  const formatDateValue = (d: Date, fmt: string) => {
    const day = String(d.getDate()).padStart(2, '0');
    const monthNum = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const monthsArr = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const monthName = monthsArr[d.getMonth()];
    const shortYear = String(year).slice(-2);

    if (fmt === 'YYYY-MM-DD') return `${year}-${monthNum}-${day}`;
    if (fmt === 'DD-MMM-YYYY') return `${day}-${monthName}-${year}`;
    if (fmt === 'MM/YY') return `${monthNum}/${shortYear}`;
    return `${day}/${monthNum}/${year}`;
  };

  // Compute Date Preview
  const datePreview = useMemo(() => {
    const today = new Date();
    const mfgDate = new Date(today);
    mfgDate.setDate(mfgDate.getDate() + dateMfgOffset);

    const expDate = new Date(mfgDate);
    expDate.setMonth(expDate.getMonth() + dateExpiryMonths);

    const mfgFormatted = formatDateValue(mfgDate, dateFormat);
    const expFormatted = formatDateValue(expDate, dateFormat);
    const batchFormatted = `${batchPrefix}${mfgDate.getFullYear()}${String(mfgDate.getMonth() + 1).padStart(2, '0')}-001`;

    return {
      mfgFormatted,
      expFormatted,
      batchFormatted
    };
  }, [dateMfgOffset, dateExpiryMonths, dateFormat, batchPrefix]);

  const handleGenerateDates = () => {
    const rowCount = csvData ? csvData.rows.length : 100;
    const newRows = csvData ? csvData.rows.map(r => ({ ...r })) : Array(rowCount).fill(null).map(() => ({}));

    const today = new Date();
    const mfgDate = new Date(today);
    mfgDate.setDate(mfgDate.getDate() + dateMfgOffset);

    const expDate = new Date(mfgDate);
    expDate.setMonth(expDate.getMonth() + dateExpiryMonths);

    const mfgStr = formatDateValue(mfgDate, dateFormat);
    const expStr = formatDateValue(expDate, dateFormat);

    for (let i = 0; i < rowCount; i++) {
      const batchStr = `${batchPrefix}${mfgDate.getFullYear()}${String(mfgDate.getMonth() + 1).padStart(2, '0')}-${String(i + 1).padStart(3, '0')}`;
      newRows[i] = { 
        ...newRows[i], 
        MfgDate: mfgStr,
        ExpiryDate: expStr,
        ...(includeBatchNo ? { BatchNo: batchStr } : {})
      };
    }

    const addedCols = includeBatchNo ? ['MfgDate', 'ExpiryDate', 'BatchNo'] : ['MfgDate', 'ExpiryDate'];
    const newHeaders = csvData 
      ? Array.from(new Set([...csvData.headers, ...addedCols])) 
      : addedCols;

    setCsvData({ headers: newHeaders, rows: newRows });

    toast.success(
      'Dates & Expiry Generated',
      `Added MfgDate (${mfgStr}) and ExpiryDate (${expStr}) across ${rowCount} records.`,
      { duration: 4000 }
    );
  };

  // --- TAB 3: BARCODE SYMBOLOGY & SCAN-VALIDATOR STUDIO STATE ---
  const [bcSymbology, setBcSymbology] = useState<'ean13' | 'upca' | 'code128' | 'gs1datamatrix' | 'microqr'>('ean13');
  const [bcTestInput, setBcTestInput] = useState('890123456789');
  const [bcColumnToScan, setBcColumnToScan] = useState<string>(csvData?.headers[0] || 'Barcode');

  // Single barcode test validation
  const testValidation = useMemo(() => {
    return validateBarcode(bcTestInput, bcSymbology);
  }, [bcTestInput, bcSymbology]);

  // Column scan report
  const columnScanReport = useMemo(() => {
    if (!csvData || !csvData.rows.length) return null;
    const targetCol = csvData.headers.includes(bcColumnToScan) ? bcColumnToScan : csvData.headers[0];
    if (!targetCol) return null;
    return validateBarcodeColumn(csvData.rows, targetCol, bcSymbology);
  }, [csvData, bcColumnToScan, bcSymbology]);

  const handleFixAllChecksums = () => {
    if (!csvData || !bcColumnToScan) return;
    const { repairedRows, repairedCount } = repairBarcodeDatasetColumn(csvData.rows, bcColumnToScan, bcSymbology);

    setCsvData({ ...csvData, rows: repairedRows });

    toast.success(
      'Checksums Repaired',
      `Auto-repaired Modulo-10 checksums for ${repairedCount} record(s) in column "${bcColumnToScan}".`,
      { duration: 4000 }
    );
  };

  return (
    <div className="flex flex-col gap-4 text-zinc-200">
      
      {/* Header Banner */}
      <div className="bg-zinc-900 p-4 rounded-xl border border-blue-500/30 flex items-center justify-between gap-3 shadow-md">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-500/20 text-blue-400 border border-blue-500/30 flex items-center justify-center shrink-0">
            <Wand2 className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-sm text-zinc-100 flex items-center gap-2">
              <span>In-App Variable Generator &amp; Barcode Studio</span>
              <span className="text-[10px] bg-blue-500/20 text-blue-400 font-mono px-2 py-0.5 rounded-full border border-blue-500/30">
                Phase 2
              </span>
            </h3>
            <p className="text-xs text-zinc-400 mt-0.5">
              Generate serial numbers, batch dates &amp; barcode symbologies instantly without needing Excel/CSV upload.
            </p>
          </div>
        </div>
      </div>

      {/* Module Tabs */}
      <div className="flex items-center gap-1 p-1 bg-zinc-950 rounded-lg border border-zinc-800">
        <button
          type="button"
          onClick={() => setActiveTab('serializer')}
          className={`flex-1 py-2 px-3 rounded-md text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 ${
            activeTab === 'serializer'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900'
          }`}
        >
          <ListOrdered className="w-4 h-4" />
          <span>Auto Serializer</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('dates')}
          className={`flex-1 py-2 px-3 rounded-md text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 ${
            activeTab === 'dates'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900'
          }`}
        >
          <Calendar className="w-4 h-4" />
          <span>Dates &amp; Expiry</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('barcodes')}
          className={`flex-1 py-2 px-3 rounded-md text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 ${
            activeTab === 'barcodes'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900'
          }`}
        >
          <Barcode className="w-4 h-4" />
          <span>Barcode Validator</span>
        </button>
      </div>

      {/* TAB 1: AUTO SEQUENCE / SERIALIZER GENERATOR */}
      {activeTab === 'serializer' && (
        <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 space-y-4 animate-in fade-in duration-200">
          
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
              <Hash className="w-4 h-4 text-blue-400" />
              1. In-App Serial Sequence Generator (Instant Series)
            </h4>
            <span className="text-[10px] text-zinc-500 font-mono">Zero Excel Required</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            
            {/* Target Column Selection */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-zinc-300">Target Column Name</label>
              <div className="flex items-center gap-2">
                <input 
                  type="text"
                  value={seqTargetCol}
                  onChange={(e) => setSeqTargetCol(e.target.value)}
                  placeholder="e.g. SerialNumber"
                  className="bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs px-3 py-2 rounded-lg outline-none focus:border-blue-500 flex-1 font-mono font-bold"
                />
              </div>
            </div>

            {/* Total Records / Sticker Count */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-zinc-300">Total Records / Sticker Quantity</label>
              <input 
                type="number"
                min="1"
                max="10000"
                value={seqRecordCount}
                onChange={(e) => setSeqRecordCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                disabled={Boolean(csvData)}
                className={`w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs px-3 py-2 rounded-lg outline-none focus:border-blue-500 font-mono font-bold ${
                  csvData ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              />
              {csvData && (
                <p className="text-[10px] text-blue-400">Locked to active dataset record count ({csvData.rows.length} rows).</p>
              )}
            </div>

            {/* Prefix */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-zinc-300">Prefix</label>
              <input 
                type="text"
                value={seqPrefix}
                onChange={(e) => setSeqPrefix(e.target.value)}
                placeholder="e.g. SN-2026-"
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs px-3 py-2 rounded-lg outline-none focus:border-blue-500 font-mono"
              />
            </div>

            {/* Suffix */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-zinc-300">Suffix</label>
              <input 
                type="text"
                value={seqSuffix}
                onChange={(e) => setSeqSuffix(e.target.value)}
                placeholder="e.g. -IND"
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs px-3 py-2 rounded-lg outline-none focus:border-blue-500 font-mono"
              />
            </div>

            {/* Start Number */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-zinc-300">Start Value</label>
              <input 
                type="number"
                value={seqStart}
                onChange={(e) => setSeqStart(parseInt(e.target.value, 10) || 1)}
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs px-3 py-2 rounded-lg outline-none focus:border-blue-500 font-mono"
              />
            </div>

            {/* Step Increment */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-zinc-300">Step / Increment</label>
              <input 
                type="number"
                min="1"
                value={seqStep}
                onChange={(e) => setSeqStep(parseInt(e.target.value, 10) || 1)}
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs px-3 py-2 rounded-lg outline-none focus:border-blue-500 font-mono"
              />
            </div>

            {/* Leading Zeros / Digit Padding */}
            <div className="col-span-1 sm:col-span-2 space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-zinc-300">Leading Zeros (Digit Padding Width)</span>
                <span className="font-mono font-bold text-blue-400">{seqPadding} Digits (e.g. {String(seqStart).padStart(seqPadding, '0')})</span>
              </div>
              <input 
                type="range"
                min="1"
                max="10"
                step="1"
                value={seqPadding}
                onChange={(e) => setSeqPadding(Number(e.target.value))}
                className="w-full accent-blue-500 cursor-pointer h-1.5 bg-zinc-800 rounded-lg"
              />
            </div>

          </div>

          {/* Live Range Preview Box */}
          <div className="bg-zinc-900/80 p-3.5 rounded-lg border border-zinc-800 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-zinc-300 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-blue-400" />
                Live Series Range Preview
              </span>
              <span className="text-[10px] bg-blue-950 text-blue-300 border border-blue-800 px-2 py-0.5 rounded font-mono">
                {sequencePreview.total.toLocaleString()} Items
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-1 font-mono text-xs">
              {sequencePreview.first3.map((item, idx) => (
                <span key={idx} className="bg-zinc-950 border border-zinc-700 text-emerald-400 px-2.5 py-1 rounded shadow-xs font-bold">
                  {item}
                </span>
              ))}

              {sequencePreview.total > 3 && (
                <>
                  <span className="text-zinc-500 font-bold px-1">...</span>
                  {sequencePreview.last3.map((item, idx) => (
                    <span key={idx} className="bg-zinc-950 border border-zinc-700 text-blue-400 px-2.5 py-1 rounded shadow-xs font-bold">
                      {item}
                    </span>
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Action Button */}
          <button
            type="button"
            onClick={handleGenerateSequence}
            className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded-lg shadow-md transition-all cursor-pointer flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Generate Series &amp; Populate Dataset ({sequencePreview.total} Records)
          </button>

        </div>
      )}

      {/* TAB 2: DYNAMIC DATE & EXPIRY BATCH GENERATOR */}
      {activeTab === 'dates' && (
        <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 space-y-4 animate-in fade-in duration-200">
          
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
              <CalendarCheck className="w-4 h-4 text-emerald-400" />
              2. Dynamic Mfg Date &amp; Expiry Batch Generator
            </h4>
            <span className="text-[10px] text-zinc-500 font-mono">Auto-Calculation</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            
            {/* Expiry Offset Presets */}
            <div className="col-span-1 sm:col-span-2 space-y-2">
              <label className="text-xs font-semibold text-zinc-300">Expiry Offset Presets</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { months: 12, label: '+12 Months (1 Yr)' },
                  { months: 24, label: '+24 Months (2 Yrs)' },
                  { months: 36, label: '+36 Months (3 Yrs)' }
                ].map((p) => (
                  <button
                    key={p.months}
                    type="button"
                    onClick={() => setDateExpiryMonths(p.months)}
                    className={`py-2 px-3 rounded-lg border text-xs font-bold transition-all cursor-pointer ${
                      dateExpiryMonths === p.months
                        ? 'bg-emerald-950 border-emerald-500 text-emerald-300 shadow-xs'
                        : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Date Format Picker */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-zinc-300">Date Format</label>
              <select
                value={dateFormat}
                onChange={(e) => setDateFormat(e.target.value as any)}
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs px-3 py-2 rounded-lg outline-none focus:border-emerald-500 font-mono"
              >
                <option value="DD/MM/YYYY">DD/MM/YYYY (e.g. 15/03/2026)</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD (ISO e.g. 2026-03-15)</option>
                <option value="DD-MMM-YYYY">DD-MMM-YYYY (e.g. 15-MAR-2026)</option>
                <option value="MM/YY">MM/YY (e.g. 03/26)</option>
              </select>
            </div>

            {/* Batch Code Prefix */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-zinc-300">Batch / Lot Prefix</label>
              <input 
                type="text"
                value={batchPrefix}
                onChange={(e) => setBatchPrefix(e.target.value)}
                placeholder="e.g. B2026-"
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs px-3 py-2 rounded-lg outline-none focus:border-emerald-500 font-mono"
              />
            </div>

          </div>

          {/* Live Calculated Date Preview Box */}
          <div className="bg-zinc-900/80 p-3.5 rounded-lg border border-zinc-800 space-y-2">
            <div className="text-xs font-semibold text-zinc-300 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-emerald-400" />
              Calculated Sample Output Across Records
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1 font-mono text-xs">
              <div className="bg-zinc-950 p-2.5 rounded border border-zinc-800 flex flex-col gap-0.5">
                <span className="text-[10px] text-zinc-500 uppercase font-sans">Mfg Date:</span>
                <span className="text-emerald-400 font-bold">{datePreview.mfgFormatted}</span>
              </div>

              <div className="bg-zinc-950 p-2.5 rounded border border-zinc-800 flex flex-col gap-0.5">
                <span className="text-[10px] text-zinc-500 uppercase font-sans">Expiry Date ({dateExpiryMonths}M):</span>
                <span className="text-pink-400 font-bold">{datePreview.expFormatted}</span>
              </div>

              <div className="bg-zinc-950 p-2.5 rounded border border-zinc-800 flex flex-col gap-0.5">
                <span className="text-[10px] text-zinc-500 uppercase font-sans">Sample Batch No:</span>
                <span className="text-blue-400 font-bold">{datePreview.batchFormatted}</span>
              </div>
            </div>
          </div>

          {/* Action Button */}
          <button
            type="button"
            onClick={handleGenerateDates}
            className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-lg shadow-md transition-all cursor-pointer flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Append MfgDate, ExpiryDate &amp; Batch Columns
          </button>

        </div>
      )}

      {/* TAB 3: EXTENDED BARCODE FORMATS & SCAN VALIDATOR STUDIO */}
      {activeTab === 'barcodes' && (
        <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 space-y-4 animate-in fade-in duration-200">
          
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-purple-400" />
              3. Extended Barcode Formats &amp; Scan-Validation Check
            </h4>
            <span className="text-[10px] text-zinc-500 font-mono">ISO/IEC Compliant</span>
          </div>

          {/* Symbology Selector */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-zinc-300">Select Barcode Symbology Format</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[
                { id: 'ean13', name: 'EAN-13', desc: '13-Digit Retail GTIN with Modulo-10 Checksum' },
                { id: 'upca', name: 'UPC-A', desc: '12-Digit North American Retail Barcode' },
                { id: 'code128', name: 'Code-128', desc: 'High-Density Alphanumeric Barcode' },
                { id: 'gs1datamatrix', name: 'GS1-DataMatrix', desc: '2D Matrix with (01)GTIN(10)LOT(17)EXP AI' },
                { id: 'microqr', name: 'Micro QR Code', desc: 'Compact 2D QR for restricted space labels' }
              ].map((sym) => (
                <button
                  key={sym.id}
                  type="button"
                  onClick={() => setBcSymbology(sym.id as any)}
                  className={`p-2.5 rounded-lg border text-left flex flex-col justify-between transition-all cursor-pointer ${
                    bcSymbology === sym.id
                      ? 'bg-purple-950/60 border-purple-500 text-white shadow-xs'
                      : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700'
                  }`}
                >
                  <span className="text-xs font-bold text-zinc-200">{sym.name}</span>
                  <span className="text-[10px] text-zinc-500 mt-1 leading-tight">{sym.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Live Scanner / Tester Input */}
          <div className="bg-zinc-900/80 p-3.5 rounded-lg border border-zinc-800 space-y-3">
            <label className="text-xs font-semibold text-zinc-300 flex items-center justify-between">
              <span>Instant Test Input &amp; Checksum Scanner</span>
              <span className="text-[10px] text-purple-400 font-mono">Live Validation</span>
            </label>

            <input 
              type="text"
              value={bcTestInput}
              onChange={(e) => setBcTestInput(e.target.value)}
              placeholder="Enter barcode string to validate..."
              className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-xs px-3 py-2 rounded-lg outline-none focus:border-purple-500 font-mono font-bold"
            />

            {/* Validation Feedback Badge */}
            <div className={`p-3 rounded-lg border flex items-start gap-2.5 text-xs ${
              testValidation.isValid
                ? 'bg-emerald-950/40 border-emerald-500/40 text-emerald-300'
                : 'bg-red-950/40 border-red-500/40 text-red-300'
            }`}>
              {testValidation.isValid ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
              ) : (
                <XCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
              )}
              <div className="space-y-1 flex-1">
                <div className="font-bold flex items-center justify-between">
                  <span>{testValidation.symbology}: {testValidation.isValid ? 'VALID ISO/IEC SPEC' : 'VALIDATION WARNING / ERROR'}</span>
                  {testValidation.repairedCode && !testValidation.isValid && (
                    <button
                      type="button"
                      onClick={() => setBcTestInput(testValidation.repairedCode!)}
                      className="text-[10px] bg-red-600 hover:bg-red-500 text-white font-bold px-2 py-0.5 rounded shadow-xs cursor-pointer flex items-center gap-1"
                    >
                      <RotateCcw className="w-3 h-3" /> Auto-Fix Checksum ({testValidation.repairedCode})
                    </button>
                  )}
                </div>
                <p className="text-[11px] leading-relaxed opacity-90">{testValidation.message}</p>
              </div>
            </div>
          </div>

          {/* Dataset Column Scan Inspector */}
          {csvData && columnScanReport && (
            <div className="bg-zinc-900/80 p-3.5 rounded-lg border border-zinc-800 space-y-3">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-zinc-300">Dataset Column Scan &amp; Bulk Repair</span>
                <select
                  value={bcColumnToScan}
                  onChange={(e) => setBcColumnToScan(e.target.value)}
                  className="bg-zinc-950 border border-zinc-700 text-zinc-200 text-xs px-2.5 py-1 rounded outline-none focus:border-purple-500 font-mono"
                >
                  {csvData.headers.map(h => (
                    <option key={h} value={h}>Scan Column: {h}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-between bg-zinc-950 p-2.5 rounded border border-zinc-800 text-xs font-mono">
                <div>
                  <span className="text-zinc-400">Total Scanned:</span> <strong className="text-zinc-200">{columnScanReport.totalRows}</strong>
                </div>
                <div>
                  <span className="text-zinc-400">Valid:</span> <strong className="text-emerald-400">{columnScanReport.validCount}</strong>
                </div>
                <div>
                  <span className="text-zinc-400">Invalid:</span> <strong className="text-red-400">{columnScanReport.invalidCount}</strong>
                </div>
                <div>
                  <span className="text-zinc-400">Compliance Score:</span> <strong className="text-purple-400">{columnScanReport.complianceScore}%</strong>
                </div>
              </div>

              {columnScanReport.invalidCount > 0 && (
                <button
                  type="button"
                  onClick={handleFixAllChecksums}
                  className="w-full py-2 bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs rounded-lg shadow-sm transition-all cursor-pointer flex items-center justify-center gap-2"
                >
                  <Wand2 className="w-4 h-4" />
                  Auto-Repair {columnScanReport.invalidCount} Invalid Checksums Across Dataset Column
                </button>
              )}
            </div>
          )}

        </div>
      )}

    </div>
  );
};
